import { useState } from 'react';

import { Icon } from '../Icon';
import { Input, type InputProps } from '../Input';
import styles from './Password.module.css';

export interface PasswordProps extends Omit<InputProps, 'suffix' | 'trailingIcon' | 'type'> {
  /** Figma Display 变体：on＝初始明文，off＝初始密文（默认）。 */
  defaultVisible?: boolean;
  /** 显隐切换后的回调；状态由组件持有，非受控。 */
  onVisibilityChange?: (visible: boolean) => void;
  /** Ant Design `visibilityToggle`：是否渲染尾部显隐切换，默认 true。 */
  visibilityToggle?: boolean;
}

/*
 * Figma `2-Input 输入框 / Password`（节点 `2941:51449`）：
 * 尾部 14px view / hide 图标切换密文与明文；三尺寸与五态全部复用 `Input`。
 * Figma 稿里的 `lock` 前缀图标 Iconfont 暂未上传，需要时用 `leadingIcon` / `prefix` 槽传入。
 */
export function Password({
  ariaLabel,
  defaultVisible = false,
  disabled,
  onVisibilityChange,
  size = 'default',
  visibilityToggle = true,
  ...inputProps
}: PasswordProps) {
  const [visible, setVisible] = useState(defaultVisible);
  const iconSize = size === 'small' ? 'xs' : 'sm';

  return (
    <Input
      ariaLabel={ariaLabel}
      disabled={disabled}
      size={size}
      type={visible ? 'text' : 'password'}
      {...inputProps}
      suffix={
        visibilityToggle ? (
          <button
            type="button"
            className={styles.toggle}
            aria-label={visible ? `隐藏${ariaLabel}` : `显示${ariaLabel}`}
            aria-pressed={visible}
            disabled={disabled}
            onClick={() => {
              const next = !visible;
              setVisible(next);
              onVisibilityChange?.(next);
            }}
          >
            <Icon name={visible ? 'hide' : 'view'} size={iconSize} />
          </button>
        ) : undefined
      }
    />
  );
}
